import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MenuItem, Stack, TextField, Typography, useTheme } from '@mui/material';
import { UseMutateFunction } from '@tanstack/react-query';
import { z } from 'zod';

import ErrorMessage from '@components/ErrorMessage';
import Loader from '@components/Loader';
import { RefetchingIndicator } from '@components/RefetchingIndicator';
import { TextFieldWithHelperSpace } from '@components/TextFieldWithHelperSpace';

import { agentConfigurationTexts } from '../constants';
import { useAllAgents } from '../services/queries';
import { AgentConfig, AgentsConfig } from '../services/types';
import { MenuOptionValue } from '../types';

const AGENT_FORM_FIELDS = {
    dropdown: 'dropdown',
    instructions: 'instructions',
    topP: 'topP',
    temperature: 'temperature',
    maxTokens: 'maxTokens',
} as const;

const AGENT_FORM_CONFIG = {
    instructions: {
        min: 1,
    },
    topP: {
        min: 0,
        max: 1,
        step: 0.01,
    },
    temperature: {
        min: 0,
        max: 1,
        step: 0.01,
    },
    maxTokens: {
        min: 500,
        max: 4096,
        step: 1,
    },
};

const NUMERIC_FIELDS_CONFIG = [
    {
        name: AGENT_FORM_FIELDS.topP,
        label: agentConfigurationTexts.topPLabel,
        config: AGENT_FORM_CONFIG.topP,
    },
    {
        name: AGENT_FORM_FIELDS.temperature,
        label: agentConfigurationTexts.temperatureLabel,
        config: AGENT_FORM_CONFIG.temperature,
    },
    {
        name: AGENT_FORM_FIELDS.maxTokens,
        label: agentConfigurationTexts.maxTokensLabel,
        config: AGENT_FORM_CONFIG.maxTokens,
    },
] as const;

const agentSchema = z.object({
    [AGENT_FORM_FIELDS.dropdown]: z.string(),
    [AGENT_FORM_FIELDS.instructions]: z.string().trim().min(AGENT_FORM_CONFIG.instructions.min, {
        message: agentConfigurationTexts.instructionsError,
    }),
    [AGENT_FORM_FIELDS.topP]: z
        .number({ invalid_type_error: agentConfigurationTexts.topPError })
        .min(AGENT_FORM_CONFIG.topP.min, {
            message: `${agentConfigurationTexts.topPMinError} ${AGENT_FORM_CONFIG.topP.min}`,
        })
        .max(AGENT_FORM_CONFIG.topP.max, {
            message: `${agentConfigurationTexts.topPMaxError} ${AGENT_FORM_CONFIG.topP.max}`,
        }),
    [AGENT_FORM_FIELDS.temperature]: z
        .number({ invalid_type_error: agentConfigurationTexts.temperatureError })
        .min(AGENT_FORM_CONFIG.temperature.min, {
            message: `${agentConfigurationTexts.temperatureMinError} ${AGENT_FORM_CONFIG.temperature.min}`,
        })
        .max(AGENT_FORM_CONFIG.temperature.max, {
            message: `${agentConfigurationTexts.temperatureMaxError} ${AGENT_FORM_CONFIG.temperature.max}`,
        }),
    [AGENT_FORM_FIELDS.maxTokens]: z
        .number({ invalid_type_error: agentConfigurationTexts.maxTokensError })
        .int({ message: agentConfigurationTexts.maxTokensIntError })
        .min(AGENT_FORM_CONFIG.maxTokens.min, {
            message: `${agentConfigurationTexts.maxTokensMinError} ${AGENT_FORM_CONFIG.maxTokens.min}`,
        })
        .max(AGENT_FORM_CONFIG.maxTokens.max, {
            message: `${agentConfigurationTexts.maxTokensMaxError} ${AGENT_FORM_CONFIG.maxTokens.max}`,
        }),
});

type AgentFormData = z.infer<typeof agentSchema>;

interface AgentFormProps {
    agents: AgentsConfig;
    mutate: UseMutateFunction<void, Error, AgentConfig>;
    isSending: boolean;
}

const AgentForm = ({ agents, mutate, isSending }: AgentFormProps) => {
    const theme = useTheme();
    const defaultAgent = agents[0];
    const { id, instructions, topP, temperature, maxTokens } = defaultAgent;

    const { watch, reset, control, handleSubmit } = useForm<AgentFormData>({
        resolver: zodResolver(agentSchema),
        mode: 'all',
        defaultValues: {
            dropdown: id,
            instructions,
            topP,
            temperature,
            maxTokens,
        },
    });

    const selectedAgentId = watch(AGENT_FORM_FIELDS.dropdown);

    useEffect(() => {
        const selectedAgent = agents.find((agent) => agent.id === selectedAgentId)!;
        const { id, instructions, topP, temperature, maxTokens } = selectedAgent;

        reset({
            dropdown: id,
            instructions,
            topP,
            temperature,
            maxTokens,
        });
    }, [selectedAgentId, reset, agents]);

    const onSubmit = ({ dropdown: id, ...rest }: AgentFormData) => {
        const selectedAgent = agents.find((agent) => agent.id === id)!;
        mutate({ ...selectedAgent, ...rest });
    };

    return (
        <Stack
            component='form'
            gap={3}
            id={MenuOptionValue.AgentConfiguration}
            onSubmit={handleSubmit(onSubmit)}
        >
            <Controller
                name={AGENT_FORM_FIELDS.dropdown}
                control={control}
                render={({ field }) => (
                    <TextField
                        select
                        label={agentConfigurationTexts.selectAgentLabel}
                        fullWidth
                        disabled={isSending}
                        {...field}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.customColors.purple,
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: theme.palette.customColors.purple,
                            },
                        }}
                    >
                        {agents.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            />
            <Controller
                name={AGENT_FORM_FIELDS.instructions}
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <TextFieldWithHelperSpace
                        label={agentConfigurationTexts.instructionsLabel}
                        fullWidth
                        rows={8}
                        multiline
                        disabled={isSending}
                        {...field}
                        fieldError={error}
                    />
                )}
            />
            {NUMERIC_FIELDS_CONFIG.map(({ name, label, config }) => (
                <Controller
                    key={name}
                    name={name}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <TextFieldWithHelperSpace
                            label={label}
                            type='number'
                            fullWidth
                            disabled={isSending}
                            {...field}
                            slotProps={{
                                htmlInput: {
                                    min: config.min,
                                    max: config.max,
                                    step: config.step,
                                },
                            }}
                            fieldError={error}
                            onChange={(e) =>
                                field.onChange((e.target as HTMLInputElement).valueAsNumber)
                            }
                        />
                    )}
                />
            ))}
        </Stack>
    );
};

interface AgentConfiguratorProps {
    mutate: UseMutateFunction<void, Error, AgentConfig>;
    isSending: boolean;
}

const AgentConfiguration = ({ mutate, isSending }: AgentConfiguratorProps) => {
    const { data, isLoading, isFetching, error, isError } = useAllAgents();

    if (isLoading) return <Loader />;

    if (isError) return <ErrorMessage error={error} />;

    if (!data) return null;

    if (data.length === 0)
        return <Typography variant='body2'>{agentConfigurationTexts.noAgentsFound}</Typography>;

    const isRefetching = !isLoading && isFetching;

    return (
        <>
            {isRefetching && <RefetchingIndicator />}
            <AgentForm agents={data} mutate={mutate} isSending={isSending} />
        </>
    );
};

export default AgentConfiguration;
